import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Circle, Workflow, AlertTriangle, Database, HelpCircle } from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";

export const Route = createFileRoute("/admin/plano-migracao-feo-crm")({
  head: () => ({
    meta: [
      { title: "Admin · Plano Migração RD CRM → FEO CRM" },
      { name: "description", content: "Plano e checklist da migração total dos leads do RD CRM para o FEO CRM." },
      { property: "og:title", content: "Admin · Migração RD CRM → FEO CRM" },
      { property: "og:description", content: "Etapas, fontes de leads e organização dos funis no FEO CRM." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/plano-migracao-feo-crm"><Plano /></AdminGate>,
});

type Etapa = { t: string; status: "ok" | "andamento" | "pendente"; itens: string[] };

const etapas: Etapa[] = [
  { t: "1 · Teste com 3 vendedoras (até 03/11)", status: "ok", itens: ["Black em Dobro dividida no mesmo rodízio: Bruna, Ingrid e Weruska no RD CRM; Daphne, Jana e Patrícia no FEO CRM"] },
  { t: "2 · Black em Dobro e BLACKFEO com as 6 vendedoras no FEO CRM", status: "ok", itens: [
    "3.934 de 3.934 leads da Black enviados ao FEO CRM (copiados do RD, que manteve os registros)",
    "Vendedora do RD preservada; leads sem vendedora distribuídos igualmente entre as 6",
    "Etapas organizadas pelas 7 colunas existentes, conforme o RD; compras aprovadas na Eduzz em Vendas",
    "Relatório do FEO CRM (04/11): 3.836 leads no Funil principal, 0 sem vendedora, 0 duplicados por e-mail/telefone (a diferença para 3.934 são cadastros repetidos unificados)",
    "Por vendedora: Bruna 578 · Daphne 689 · Ingrid 583 · Jana 696 · Patrícia 699 · Weruska 590",
    "Por etapa: Sem Contato 214 · Buscando Contato 1.597 · Em Negociação 69 · Fechamento 26 · Nutrição 269 · Vendas 471 · Perdidos 1.190",
  ] },
  { t: "3 · Demais fontes no FEO CRM (desde 04/11)", status: "ok", itens: [
    "FEO CRM com 3 funis, todos com as mesmas 7 etapas: Funil principal, Aquecimento/Materiais e Universitários",
    "Aulas experimentais, ementas, funis de materiais, revista e imersão → Aquecimento/Materiais",
    "Quizzes (Pós e Dor), pré-checkout das pós e Black → Funil principal",
    "Estudantes de qualquer fonte → Universitários",
    "Todos entram em Sem contato, num rodízio único e igual entre as 6 vendedoras",
    "Frios não-estudantes seguem só no e-mail marketing (RD Station); STAR, certificações e Agente ficam fora (CRM focado em pós)",
    "Dados enviados: origem, pós de interesse, temperatura, respostas, página, UTMs e data de captura",
    "Compra aprovada na Eduzz move o lead para Vendas no próprio funil, mantém a vendedora e encerra as cadências",
    "18 leads de 04/11 que caíram no RD CRM antes da publicação foram reenviados ao FEO CRM com a mesma vendedora (16 em Aquecimento/Materiais e 2 pré-checkouts no Funil principal)",
  ] },
  { t: "4 · Histórico antigo do RD", status: "ok", itens: ["Decidido: não copiar. O que já está no RD fica como está; vale a partir de 04/11"] },
  { t: "5 · Desligar o RD CRM", status: "ok", itens: ["Nenhuma captura envia mais ao RD CRM; o RD Station de e-mail marketing e as tags continuam iguais"] },
  { t: "6 · Acompanhamento", status: "ok", itens: [
    "Conferência diária pelo painel de Leads (coluna CRM mostra FEO CRM e a vendedora) × quadro do FEO CRM",
    "11 leads ganhos no RD sem compra na Eduzz: confirmados como vendas externas (ficam em Fechamento, sem ação pendente)",
    "Correção de 04/11: estudantes eram recusados pelo FEO CRM por texto de formação diferente; o site agora envia só \"Sou estudante de fisioterapia\" ou \"Sou fisioterapeuta formado\" e o único lead afetado foi reenviado (Universitários, Jana)",
    "Rodízio conferido no banco: ciclo igual de 0 a 5 (Bruna, Daphne, Ingrid, Jana, Weruska, Patrícia). A concentração inicial em Bruna/Ingrid/Weruska veio só dos 18 leads da madrugada reenviados com a vendedora original",
  ] },
  { t: "7 · Revisão final com o FEO CRM (04/11)", status: "ok", itens: [
    "Revisão cruzada site × FEO CRM concluída sem divergências: entrada, cabeçalho de segurança, campos, formação, funis, reenvio simplificado em caso de recusa",
    "Torneira do RD CRM fechada: 100% das novas capturas vão só para o FEO CRM",
    "STAR, certificações e Agente nunca entram no CRM (venda direta na Eduzz)",
    "FEO CRM publicado, 3 funis operando e entrada pronta para o fluxo contínuo",
    "Daqui pra frente: acompanhar a chegada dos novos leads no Funil principal e conferir a distribuição igual entre as 6 vendedoras",
  ] },
];

const fontes: [string, string, string, string][] = [
  ["Funis de materiais", "4.914", "83", "FEO CRM · Aquecimento/Materiais (frios só e-mail)"],
  ["Aulas experimentais", "1.744", "63", "FEO CRM · Aquecimento/Materiais"],
  ["Revista", "560", "9", "FEO CRM · Aquecimento/Materiais"],
  ["Imersão PBE", "428", "5", "FEO CRM · Aquecimento/Materiais"],
  ["Quizzes (Pós e Dor)", "427", "5", "FEO CRM · Funil principal"],
  ["Ementas", "223", "27", "FEO CRM · Aquecimento/Materiais"],
  ["Pré-checkout das pós", "~30", "todos", "FEO CRM · Funil principal"],
  ["Pré-checkout STAR / certificações / Agente", "~50", "0", "Nenhum CRM (venda direta)"],
];

const perguntas = [
  "Funis: 3 separados (Principal, Aquecimento/Materiais, Universitários)",
  "Estudantes: rodízio entre as 6 vendedoras",
  "Histórico do RD: não copiar; vale a partir de 04/11",
  "STAR, certificações e Agente: fora do CRM",
];

function Card({ title, icon: Icon, children }: { title: string; icon: typeof Workflow; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <h2 className="flex items-center gap-2 text-lg font-bold text-white mb-4"><Icon className="h-5 w-5 text-[#f5b022]" />{title}</h2>
      {children}
    </section>
  );
}

function Plano() {
  return (
    <div className="min-h-screen px-4 py-12">
      <div className="mx-auto max-w-4xl space-y-6">
        <Link to="/admin/planos" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> Projetos/Implementações</Link>
        <header>
          <p className="text-xs uppercase tracking-widest text-[#10b981]">Concluído em 04/11/2026</p>
          <h1 className="text-3xl font-bold text-white mt-1">Migração total RD CRM → FEO CRM</h1>
          <p className="text-white/70 mt-2">Todos os leads de todos os fluxos no FEO CRM, divididos igualmente entre as 6 vendedoras.</p>
        </header>

        <Card title="Etapas e checklist" icon={Workflow}>
          <div className="space-y-4">
            {etapas.map((e) => (
              <div key={e.t} className="rounded-lg bg-white/5 p-4">
                <p className="text-sm font-semibold text-white">{e.t} <span className="ml-2 text-xs" style={{ color: e.status === "ok" ? "#10b981" : e.status === "andamento" ? "#f5b022" : "#8b9bb5" }}>{e.status === "ok" ? "Concluída" : e.status === "andamento" ? "Em andamento" : "A fazer"}</span></p>
                <ul className="mt-2 space-y-1.5">
                  {e.itens.map((i) => {
                    const pend = i.startsWith("Pendente") || e.status === "pendente";
                    return <li key={i} className="flex gap-2 text-sm text-white/80">{pend ? <Circle className="h-4 w-4 mt-0.5 shrink-0 text-[#8b9bb5]" /> : <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#10b981]" />}{i}</li>;
                  })}
                </ul>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Fontes de leads (volume 01/10 a 03/11)" icon={Database}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-white/80">
              <thead><tr className="text-left text-xs text-white/50"><th className="py-2">Fonte</th><th>Leads</th><th>Já no FEO</th><th>Destino desde 04/11</th></tr></thead>
              <tbody>{fontes.map(([a, b, c, d]) => <tr key={a} className="border-t border-white/10"><td className="py-2">{a}</td><td>{b}</td><td>{c}</td><td>{d}</td></tr>)}</tbody>
            </table>
          </div>
        </Card>

        <Card title="Funis no FEO CRM (aprovado)" icon={Workflow}>
          <ul className="space-y-2 text-sm text-white/80">
            <li><b className="text-white">Funil Principal (vendas):</b> pré-checkout, quizzes quentes/mornos e Black</li>
            <li><b className="text-white">Funil Aquecimento/Materiais:</b> aulas experimentais, ementas, funis, revista e imersão</li>
            <li><b className="text-white">Funil Universitários:</b> estudantes</li>
            <li>Todos com as mesmas 7 etapas e rodízio igual entre as 6 vendedoras. Frios não-estudantes continuam só no e-mail marketing.</li>
            
          </ul>
        </Card>

        <Card title="Decisões tomadas" icon={HelpCircle}>
          <ul className="space-y-2">{perguntas.map((p) => <li key={p} className="flex gap-2 text-sm text-white/80"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[#f5b022]" />{p}</li>)}</ul>
        </Card>
      </div>
    </div>
  );
}
